import { Link, useNavigate } from "react-router-dom";
import { Minus, Plus, X, ArrowRight } from "lucide-react";
import { useStore } from "../context/StoreContext";
import { inr } from "../lib/api";
import { FadeUp } from "../components/Reveal";

export default function Cart() {
  const { cart, updateQty, removeFromCart, cartTotal } = useStore();
  const navigate = useNavigate();

  if (cart.length === 0) {
    return (
      <div className="pt-20 min-h-[70vh] grid place-items-center px-6 text-center" data-testid="cart-empty">
        <div>
          <p className="overline text-gold-deep mb-4">Your Bag</p>
          <h1 className="font-serif text-5xl md:text-7xl tracking-tight mb-6">Nothing here yet</h1>
          <p className="text-ink-soft mb-8">Every great wardrobe begins with a single piece.</p>
          <Link to="/shop" className="btn-ink inline-flex items-center gap-2 px-10 py-4 overline">Explore the atelier <ArrowRight size={14} /></Link>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-20" data-testid="cart-page">
      <div className="px-5 md:px-10 pt-14 pb-10 max-w-[1400px] mx-auto">
        <FadeUp><p className="overline text-gold-deep mb-3">Your Bag</p></FadeUp>
        <h1 className="font-serif text-5xl md:text-7xl tracking-tight">Shopping Bag ({cart.length})</h1>
      </div>

      <div className="px-5 md:px-10 max-w-[1400px] mx-auto grid lg:grid-cols-[1fr_380px] gap-14 pb-28">
        <div className="divide-y divide-line border-y border-line">
          {cart.map((item) => (
            <div key={item.key} className="flex gap-5 py-6" data-testid={`cart-row-${item.slug}`}>
              <Link to={`/product/${item.slug}`}>
                <img src={item.image} alt={item.name} className="h-40 w-32 object-cover bg-[#efece6]" />
              </Link>
              <div className="flex flex-1 flex-col">
                <div className="flex justify-between">
                  <div>
                    <Link to={`/product/${item.slug}`}><h3 className="font-serif text-2xl leading-tight">{item.name}</h3></Link>
                    <p className="overline text-ink-soft mt-2">Size {item.size}</p>
                  </div>
                  <button onClick={() => removeFromCart(item.key)} data-testid={`cart-remove-${item.slug}`}><X size={18} className="text-ink-soft hover:text-ink" /></button>
                </div>
                <div className="mt-auto flex items-center justify-between">
                  <div className="flex items-center border border-line">
                    <button className="px-3 py-2" onClick={() => updateQty(item.key, item.quantity - 1)}><Minus size={12} /></button>
                    <span className="w-10 text-center text-sm">{item.quantity}</span>
                    <button className="px-3 py-2" onClick={() => updateQty(item.key, item.quantity + 1)}><Plus size={12} /></button>
                  </div>
                  <span>{inr(item.price * item.quantity)}</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="lg:sticky lg:top-28 self-start bg-[#f2efe9] p-8">
          <h2 className="font-serif text-3xl tracking-tight mb-6">Summary</h2>
          <div className="space-y-3 text-sm border-b border-line pb-5">
            <div className="flex justify-between"><span className="text-ink-soft">Subtotal</span><span>{inr(cartTotal)}</span></div>
            <div className="flex justify-between"><span className="text-ink-soft">Shipping</span><span>Complimentary</span></div>
          </div>
          <div className="flex justify-between text-lg py-5">
            <span className="font-serif text-2xl">Total</span>
            <span className="font-serif text-2xl">{inr(cartTotal)}</span>
          </div>
          <button onClick={() => navigate("/checkout")} className="btn-ink w-full py-4 overline" data-testid="cart-checkout-btn">Proceed to Checkout</button>
          <Link to="/shop" className="mt-4 block text-center overline link-underline text-gold-deep">Continue shopping</Link>
        </div>
      </div>
    </div>
  );
}
